/***
 *
 *   TICKET LIST VIEW
 *   New view over existing ticket data. Ported from
 *   ui-reference/src/components/tickets/ticket-list-view.tsx — without
 *   @tanstack/react-table (no new deps): plain sortable table + Paginate.
 *
 *   PROPS
 *   tickets: live-query result ({ data, status })
 *   onCreate: open create-ticket modal
 *   filtered: whether any filters are active (empty-state copy)
 *
 **********/

import { useEffect, useMemo, useState } from 'react';
import {
    EmptyState,
    Skeleton,
    Paginate,
    Icon,
    cn,
} from 'components/lib';
import { useSearchParams } from 'react-router-dom';
import { useTranslate } from 'app/translations';
import { PriorityBadge } from 'components/tickets/priorityBadge';
import { StatusBadge } from 'components/tickets/statusBadge';
import { AssigneeAvatars } from 'components/tickets/assigneeAvatars';
import { isTicketOverdue } from 'utilities/ticketDue';
import { findCurrStatus } from 'utilities/tickets';
import * as dayjs from 'dayjs';

const PAGE_SIZE = 10;

function sortTickets(list, sortKey, sortDir) {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
        let av;
        let bv;
        switch (sortKey) {
            case 'number':
                av = a.number;
                bv = b.number;
                break;
            case 'subject':
                av = (a.subject || a.text || '').toLowerCase();
                bv = (b.subject || b.text || '').toLowerCase();
                break;
            case 'priority': {
                const order = { urgent: 0, high: 1, medium: 2, low: 3 };
                av = order[a.priority] ?? 9;
                bv = order[b.priority] ?? 9;
                break;
            }
            case 'due_date':
                av = a.due_date ? new Date(a.due_date).getTime() : Infinity;
                bv = b.due_date ? new Date(b.due_date).getTime() : Infinity;
                break;
            case 'status':
                av = findCurrStatus(a) || '';
                bv = findCurrStatus(b) || '';
                break;
            default:
                av = a.date_created ? new Date(a.date_created).getTime() : 0;
                bv = b.date_created ? new Date(b.date_created).getTime() : 0;
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
    });
}

export function TicketListView(props) {
    const { _t } = useTranslate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sortKey, setSortKey] = useState('number');
    const [sortDir, setSortDir] = useState('desc');
    const [offset, setOffset] = useState(0);

    const raw = (props.tickets?.data || []).filter((t) => !t.is_archived);
    const isLoading = props.tickets?.status === 'loading' || props.tickets?.isLoading;

    useEffect(() => {
        setOffset(0);
    }, [props.tickets?.data, sortKey, sortDir]);

    const sorted = useMemo(() => sortTickets(raw, sortKey, sortDir), [raw, sortKey, sortDir]);
    const page = sorted.slice(offset, offset + PAGE_SIZE);

    function openTicket(number) {
        searchParams.set('id', number);
        setSearchParams(searchParams);
    }

    function toggleSort(key) {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    }

    if (isLoading) {
        return (
            <div className="flex min-h-0 flex-1 flex-col basis-0">
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full rounded" />
                    ))}
                </div>
            </div>
        );
    }

    if (raw.length === 0) {
        return (
            <EmptyState
                className="min-h-0 flex-1 border-none bg-transparent"
                bare
                title={
                    props.filtered
                        ? _t('ticket.noTicketsFiltered')
                        : _t('ticket.noTickets')
                }
                description={
                    props.filtered
                        ? _t('ticket.noTicketsFilteredDescription')
                        : _t('ticket.noTicketsDescription')
                }
                buttonText={_t('ticket.create.title')}
                action={props.onCreate}
            />
        );
    }

    const headers = [
        { key: 'number', label: '#', sortable: true },
        { key: 'subject', label: _t('ticket.list.subject'), sortable: true },
        { key: 'priority', label: _t('priority.label'), sortable: true },
        { key: 'status', label: _t('ticket.list.status'), sortable: true },
        { key: 'assignees', label: _t('assignee.multiple'), sortable: false },
        { key: 'category', label: _t('ticket.category.label'), sortable: false },
        { key: 'due_date', label: _t('ticket.list.dueDate'), sortable: true },
        { key: 'comments', label: '', sortable: false },
    ];

    return (
        <div className="flex min-h-0 flex-1 flex-col basis-0">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
                {/* Mobile cards */}
                <div className="space-y-2 md:hidden">
                    {page.map((ticket) => {
                        const statusKey = findCurrStatus(ticket);
                        return (
                            <button
                                key={ticket.id || ticket.number}
                                type="button"
                                onClick={() => openTicket(ticket.number)}
                                className="w-full rounded-lg border bg-surface p-3 text-left">
                                <div className="mb-1 flex items-center justify-between">
                                    <span className="font-mono text-xs text-text-muted">
                                        {ticket.number}
                                    </span>
                                    <PriorityBadge priority={ticket.priority} />
                                </div>
                                <p className="mb-2 text-sm font-medium text-text-primary">
                                    {ticket.subject || ticket.text}
                                </p>
                                <div className="flex items-center justify-between">
                                    <StatusBadge status={statusKey} />
                                    <AssigneeAvatars assignees={ticket.ticket_assignee || []} max={2} />
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Desktop table */}
                <div className="hidden overflow-x-auto rounded-lg border md:block">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/50">
                                {headers.map((h) => (
                                    <th
                                        key={h.key}
                                        className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                                        {h.sortable ? (
                                            <button
                                                type="button"
                                                className="flex items-center gap-1 hover:text-text-primary"
                                                onClick={() => toggleSort(h.key)}>
                                                {h.label || (
                                                    <Icon image="message-square" size={14} color="currentColor" />
                                                )}
                                                <Icon image="sliders" size={12} color="currentColor" />
                                            </button>
                                        ) : h.key === 'comments' ? (
                                            <Icon image="message-square" size={14} color="currentColor" />
                                        ) : (
                                            h.label
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {page.map((ticket) => {
                                const statusKey = findCurrStatus(ticket);
                                const overdue = isTicketOverdue({
                                    due_date: ticket.due_date,
                                    status: statusKey,
                                });
                                const comments = (ticket.histories || []).filter(
                                    (h) => h.action_type === 'comment_posted',
                                ).length;
                                return (
                                    <tr
                                        key={ticket.id || ticket.number}
                                        className="cursor-pointer border-b last:border-b-0 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                                        onClick={() => openTicket(ticket.number)}>
                                        <td className="px-3 py-2.5 font-mono text-xs text-text-muted">
                                            {ticket.number}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className="line-clamp-1 font-medium text-text-primary">
                                                {ticket.subject || ticket.text}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <PriorityBadge priority={ticket.priority} />
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <StatusBadge status={statusKey} />
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <AssigneeAvatars
                                                assignees={ticket.ticket_assignee || []}
                                                max={3}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-sm text-text-secondary">
                                            {ticket.ticket_category?.name || '—'}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {ticket.due_date ? (
                                                <span
                                                    className={cn(
                                                        'flex items-center gap-1 text-sm',
                                                        overdue
                                                            ? 'font-semibold text-error'
                                                            : 'text-text-secondary',
                                                    )}>
                                                    <Icon image="calendar" size={12} color="currentColor" />
                                                    {dayjs(ticket.due_date).format('MMM D')}
                                                </span>
                                            ) : (
                                                <span className="text-text-muted">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-sm text-text-muted">
                                            {comments}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <Paginate
                total={sorted.length}
                limit={PAGE_SIZE}
                offset={offset}
                onChange={setOffset}
            />
        </div>
    );
}
